import { useMemo } from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import { cn } from '@/utils/cn'
import { getSparklineYBounds } from '@/utils/kpiTrend'

interface KpiSparklineProps {
  data: number[]
  className?: string
  strokeColor?: string
  fillColor?: string
  delay?: number
}

export function KpiSparkline({
  data,
  className,
  strokeColor = 'var(--brand-primary)',
  fillColor = 'rgba(152, 125, 232, 0.12)',
  delay = 0,
}: KpiSparklineProps) {
  const reduced = useReducedMotion()
  const width = 120
  const height = 52

  const paths = useMemo(() => {
    if (!data || data.length < 2) return null

    const { min, max } = getSparklineYBounds(data)
    const range = max - min || 1
    const stepX = width / (data.length - 1)

    const points = data.map((v, i) => {
      const x = i * stepX
      const y = height - ((v - min) / range) * (height - 8) - 4
      return { x, y }
    })

    const line = points
      .map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`)
      .join(' ')
    const fill = `${line} L${width},${height} L0,${height} Z`
    const last = points[points.length - 1]

    return { line, fill, last }
  }, [data, width, height])

  if (!paths) return null

  const transition = reduced
    ? { duration: 0 }
    : { duration: 2.4, delay: delay / 1000, ease: [0.22, 1, 0.36, 1] as const }

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio='xMidYMid meet'
      className={cn('h-full w-full', className)}
      aria-hidden
    >
      <motion.path
        d={paths.fill}
        fill={fillColor}
        fillOpacity={0.35}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ ...transition, duration: 0.5 }}
      />
      <motion.path
        d={paths.line}
        fill='none'
        stroke={strokeColor}
        strokeWidth={2}
        strokeLinecap='round'
        strokeLinejoin='round'
        initial={reduced ? false : { pathLength: 0, opacity: 0 }}
        animate={{ pathLength: 1, opacity: 1 }}
        transition={transition}
      />
      <motion.circle
        cx={paths.last.x}
        cy={paths.last.y}
        r={3}
        fill={strokeColor}
        initial={reduced ? false : { scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ ...transition, delay: (delay + 900) / 1000 }}
      />
    </svg>
  )
}
